import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
  Switch,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import { jobDescriptionService } from '../services/jobDescriptionService';
import { EventNote, WorkOutline } from '@mui/icons-material';

const defaultForm = {
  event: false,
  title: '',
  description: '',
  jobType: '',
  jobCategory: '',
  department: '',
  employmentLevel: '',
  industry: '',
  postingDate: '',
  closingDate: '',
  numberOfOpenings: 1,
  workLocationType: '',
  country: '',
  state: '',
  city: '',
  zipPostalCode: '',
  officeAddress: '',
  region: '',
  salaryMin: 0,
  salaryMax: 0,
  salaryCurrency: 'USD',
  salaryType: 'Annual',
  bonusEligibility: false,
  benefitsDescription: '',
  minimumEducationLevel: '',
  requiredExperienceYears: 0,
  requiredSkills: [],
  preferredSkills: [],
  applicationURL: '',
  recruiterName: '',
  recruiterEmail: '',
  companyID: '',
};

const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Internship'];
const salaryTypes = ['Annual', 'Monthly', 'Weekly', 'Hourly'];
const employmentLevels = ['Entry', 'Mid', 'Senior', 'Executive'];
const workLocationTypes = ['Remote', 'Hybrid', 'On-site'];

const isValidEmail = (email: string) => !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const JDCreationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  initialData?: any;
  infoMessage?: string;
}> = ({ open, onClose, onCreated, initialData, infoMessage }) => {
  const [form, setForm] = useState({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Prefill form if initialData is provided (for clone)
  React.useEffect(() => {
    if (open && initialData) {
      setForm({
        ...defaultForm,
        ...initialData,
        requiredSkills: initialData.requiredSkills || [],
        preferredSkills: initialData.preferredSkills || [],
      });
      // Set the input values from arrays
      setSkillsInputValue((initialData.requiredSkills || []).join(', '));
      setPreferredSkillsInputValue((initialData.preferredSkills || []).join(', '));
    } else if (open) {
      setForm({ ...defaultForm });
      setSkillsInputValue('');
      setPreferredSkillsInputValue('');
      setSelectedMode('jd');
    }
    setError(null);
    setSuccessMessage(null);
  }, [open, initialData]);

  const [skillsInputValue, setSkillsInputValue] = useState('');
  const [preferredSkillsInputValue, setPreferredSkillsInputValue] = useState('');
  const [selectedMode, setSelectedMode] = useState<'jd' | 'event'>('jd');

  const isEventMode = selectedMode === 'event';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSkillChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSkillsInputValue(value);

    setForm(prev => ({
      ...prev,
      requiredSkills: value
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0),
    }));
  };

  const handlePreferredSkillChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPreferredSkillsInputValue(value);

    setForm(prev => ({
      ...prev,
      preferredSkills: value
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (form.recruiterEmail && !isValidEmail(form.recruiterEmail)) {
      setError('Recruiter Email must be a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        event: isEventMode,
        type: selectedMode, // 'jd' or 'event'
        jobTitle: form.title,
        jobDescription: form.description,
        jobType: selectedMode === 'event' ? 'Full-time' : form.jobType,
        jobCategory: form.jobCategory,
        department: form.department,
        employmentLevel: selectedMode === 'event' ? 'Multiple' : form.employmentLevel,
        industry: form.industry,
        postingDate: form.postingDate,
        closingDate: form.closingDate,
        numberOfOpenings: Number(form.numberOfOpenings),
        // Location fields (flattened)
        workLocationType: selectedMode === 'event' ? 'Hybrid' : form.workLocationType,
        country: form.country,
        state: form.state,
        city: form.city,
        zipPostalCode: form.zipPostalCode,
        officeAddress: form.officeAddress,
        region: form.region,
        // Salary fields (flattened)
        salaryMin: Number(form.salaryMin),
        salaryMax: Number(form.salaryMax),
        salaryCurrency: form.salaryCurrency,
        salaryType: form.salaryType,
        bonusEligibility: form.bonusEligibility,
        benefitsDescription: form.benefitsDescription,
        // Requirements fields (flattened)
        minimumEducationLevel: form.minimumEducationLevel,
        requiredExperienceYears: Number(form.requiredExperienceYears),
        requiredSkills: form.requiredSkills,
        preferredSkills: form.preferredSkills,
        // Application details (flattened)
        applicationURL: form.applicationURL,
        recruiterName: form.recruiterName,
        recruiterEmail: form.recruiterEmail,

        // Company field (flattened)
        companyID: form.companyID,
      };

      await jobDescriptionService.add(payload);

      // Show success message
      setSuccessMessage(initialData ? 'Job cloned successfully!' : 'Job created successfully!');

      // Reset form
      setForm({ ...defaultForm });

      // Close modal after a short delay to show success message
      setTimeout(() => {
        onCreated();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
          Create New Opportunity
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={selectedMode}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setSelectedMode(value);
                setForm(prev => ({ ...prev, event: value === 'event' }));
              }
            }}
            sx={{
              gap: 2, // Space between buttons
              '& .MuiToggleButton-root': {
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'grey.300',
                backgroundColor: 'transparent',
                color: 'text.primary',
                transition: 'all 0.2s ease-in-out', // ✅ Smooth transition

                '&.Mui-selected': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                  transform: 'scale(1.02)', // ✅ Slight scale when selected
                },

                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'transparent', // ✅ No background color
                  transform: 'scale(1.05)', // ✅ Pop effect on hover
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)', // ✅ Subtle shadow
                },

                '&.Mui-selected:hover': {
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                  transform: 'scale(1.08)', // ✅ Slightly bigger pop when selected + hover
                  boxShadow: '0 6px 16px rgba(33, 150, 243, 0.2)', // ✅ More pronounced shadow
                },
              },
            }}
          >
            <ToggleButton value="jd">
              <WorkOutline sx={{ mr: 1 }} />
              Job Description
            </ToggleButton>
            <ToggleButton value="event">
              <EventNote sx={{ mr: 1 }} />
              Event-Based
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />
      </DialogTitle>

      {infoMessage && (
        <Box mb={2} mx={3}>
          <Chip color="info" label={infoMessage} />
        </Box>
      )}
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {/* Show success message */}
          {successMessage && (
            <Box mb={2}>
              <Chip color="success" label={successMessage} />
            </Box>
          )}
          <Grid container spacing={2}>
            {isEventMode ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Event Name"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Offering"
                    name="department"
                    value={form.department}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="From Date"
                    name="postingDate"
                    type="date"
                    value={form.postingDate}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="To Date"
                    name="closingDate"
                    type="date"
                    value={form.closingDate}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Job Title"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Department"
                    name="department"
                    value={form.department}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Job Description"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    required={!isEventMode}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Job Type"
                    name="jobType"
                    value={form.jobType}
                    onChange={handleChange}
                    required
                    fullWidth
                  >
                    {jobTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Job Category"
                    name="jobCategory"
                    value={form.jobCategory}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Employment Level"
                    name="employmentLevel"
                    value={form.employmentLevel}
                    onChange={handleChange}
                    required
                    fullWidth
                  >
                    {employmentLevels.map(level => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Industry"
                    name="industry"
                    value={form.industry}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Posting Date"
                    name="postingDate"
                    type="date"
                    value={form.postingDate}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Closing Date"
                    name="closingDate"
                    type="date"
                    value={form.closingDate}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Number of Openings"
                    name="numberOfOpenings"
                    type="number"
                    value={form.numberOfOpenings}
                    onChange={handleChange}
                    required
                    fullWidth
                    inputProps={{
                      min: 1,
                      step: 1,
                      onKeyDown: e => {
                        if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      },
                    }}
                    error={form.numberOfOpenings <= 0}
                    helperText={form.numberOfOpenings <= 0 ? 'Must be at least 1' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Work Location Type"
                    name="workLocationType"
                    value={form.workLocationType}
                    onChange={handleChange}
                    required
                    fullWidth
                  >
                    {workLocationTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Country"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="State"
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Zip/Postal Code"
                    name="zipPostalCode"
                    value={form.zipPostalCode}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Office Address"
                    name="officeAddress"
                    value={form.officeAddress}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Region"
                    name="region"
                    value={form.region}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Salary Min"
                    name="salaryMin"
                    type="number"
                    value={form.salaryMin}
                    onChange={handleChange}
                    required
                    fullWidth
                    inputProps={{
                      min: 0,
                      step: 1000,
                      onKeyDown: e => {
                        if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      },
                    }}
                    error={form.salaryMin < 0}
                    helperText={form.salaryMin < 0 ? 'Cannot be negative' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Salary Max"
                    name="salaryMax"
                    type="number"
                    value={form.salaryMax}
                    onChange={handleChange}
                    required
                    fullWidth
                    inputProps={{
                      min: 0,
                      step: 1000,
                      onKeyDown: e => {
                        if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      },
                    }}
                    error={
                      form.salaryMax < 0 || (form.salaryMax > 0 && form.salaryMax < form.salaryMin)
                    }
                    helperText={
                      form.salaryMax < 0
                        ? 'Cannot be negative'
                        : form.salaryMax > 0 && form.salaryMax < form.salaryMin
                          ? 'Maximum must be greater than minimum'
                          : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Salary Currency"
                    name="salaryCurrency"
                    value={form.salaryCurrency}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Salary Type"
                    name="salaryType"
                    value={form.salaryType}
                    onChange={handleChange}
                    required
                    fullWidth
                  >
                    {salaryTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="bonusEligibility"
                        checked={form.bonusEligibility}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Bonus Eligibility"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Benefits Description"
                    name="benefitsDescription"
                    value={form.benefitsDescription}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Minimum Education Level"
                    name="minimumEducationLevel"
                    value={form.minimumEducationLevel}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Required Experience Years"
                    name="requiredExperienceYears"
                    type="number"
                    value={form.requiredExperienceYears}
                    onChange={handleChange}
                    required
                    fullWidth
                    inputProps={{
                      min: 0,
                      max: 50,
                      step: 1,
                      onKeyDown: e => {
                        if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      },
                    }}
                    error={form.requiredExperienceYears < 0}
                    helperText={form.requiredExperienceYears < 0 ? 'Cannot be negative' : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Required Skills (comma separated)"
                    name="requiredSkills"
                    value={skillsInputValue} // ✅ Use raw string value
                    onChange={handleSkillChange}
                    required
                    fullWidth
                    placeholder="e.g., JavaScript, React, Node.js, TypeScript"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Preferred Skills (comma separated)"
                    name="preferredSkills"
                    value={preferredSkillsInputValue} // ✅ Use raw string value
                    onChange={handlePreferredSkillChange}
                    fullWidth
                    placeholder="e.g., AWS, Docker, Kubernetes, MongoDB"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Application URL"
                    name="applicationURL"
                    value={form.applicationURL}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Recruiter Name"
                    name="recruiterName"
                    value={form.recruiterName}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Recruiter Email"
                    name="recruiterEmail"
                    value={form.recruiterEmail}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Company ID"
                    name="companyID"
                    value={form.companyID}
                    onChange={handleChange}
                    required
                    fullWidth
                  />
                </Grid>
              </>
            )}
          </Grid>

          {error && (
            <Box mt={2}>
              <Chip color="error" label={error} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              background: 'var(--primary-blue)',
              color: '#fff',
              '&:hover': { background: 'var(--primary-blue)', opacity: 0.9 },
            }}
            disabled={loading}
          >
            {loading
              ? initialData
                ? 'Cloning...'
                : 'Creating...'
              : initialData
                ? 'Clone'
                : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default JDCreationDialog;
